import type {
  QueueItem,
  Asistente,
  RegisterData,
  PrefixUpdateData,
  ApproveCategoryData,
  ChangeCategoryData,
} from "../types"
import type { DynamicCreateRequest, DynamicUpdatePayload } from "../types/dynamic"
import { updateAsistente, registerSimple, updatePrefix, approveCategory, changeCategory, createDynamicUser, updateDynamicUserByUniqueField } from "./api"
import { addToQueueDB, getQueueFromDB, removeFromQueueDB, updateAsistenteInDB } from "./database"

// Callback para notificar cambios en la cola
let queueUpdateCallback: () => void = () => {}

export const setQueueUpdateCallback = (callback: () => void) => {
  queueUpdateCallback = callback
}

export const addToQueue = async (item: Omit<QueueItem, "id">): Promise<void> => {
  try {
    await addToQueueDB(item)

    // Update local database immediately for optimistic updates (only for asistente updates)
    if ((item.type === "update" || item.type === "print" || item.type === "kit") && "asistente_folio" in item.data) {
      await updateAsistenteInDB(item.data as Asistente)
    }

    // Get identifier based on data type
    let identifier = ""
    if ("asistente_folio" in item.data) {
      identifier = item.data.asistente_folio
    } else if ("folio" in item.data) {
      identifier = item.data.folio
    } else if ("asistente_id" in item.data) {
      identifier = item.data.asistente_id
    }

    console.log(`📝 Elemento agregado a la cola: ${item.type} para ${identifier}`)
    
    // Notificar cambio en la cola
    queueUpdateCallback()
  } catch (error) {
    console.error("❌ Error agregando elemento a la cola:", error)
    throw error
  }
}

export const getQueueCount = async (): Promise<number> => {
  try {
    const queue = await getQueueFromDB()
    return queue.length
  } catch (error) {
    console.error("❌ Error obteniendo contador de cola:", error)
    return 0
  }
}

export const processQueue = async (): Promise<{ success: boolean; processed: number; error?: string }> => {
  try {
    const queue = await getQueueFromDB()

    if (queue.length === 0) {
      return { success: true, processed: 0 }
    }

    console.log(`🔄 Procesando ${queue.length} elementos en cola...`)

    let processed = 0
    let lastError: string | null = null

    for (const item of queue) {
      try {
        // Get identifier based on data type
        let identifier = ""
        if ("asistente_folio" in item.data) {
          identifier = item.data.asistente_folio
        } else if ("folio" in item.data) {
          identifier = item.data.folio
        } else if ("asistente_id" in item.data) {
          identifier = item.data.asistente_id
        }

        console.log(`⏳ Procesando ${item.type} para ${identifier}...`)

        if (item.type === "register") {
          // Handle registration
          const registerData = item.data as RegisterData
          const result = await registerSimple(registerData)
          
          // If it's a category 4 (Médico Certificado) registration and we get asistente_id, approve automatically
          if (registerData.category_id === 4 && result.success && result.asistente_id) {
            try {
              console.log(`🏥 Auto-aprobando categoría para médico certificado: ${result.asistente_id}`)
              await approveCategory({ asistente_id: result.asistente_id })
              console.log(`✅ Categoría aprobada automáticamente para: ${result.asistente_id}`)
            } catch (approveError) {
              console.error(`❌ Error auto-aprobando categoría para ${result.asistente_id}:`, approveError)
              // Don't fail the whole process, just log the error
            }
          }
        } else if (item.type === "create_dynamic_user") {
          // Handle dynamic user creation
          const dynamicCreateData = item.data as DynamicCreateRequest
          identifier = `${dynamicCreateData.nombre} ${dynamicCreateData.apellido} (${dynamicCreateData.email})`
          const result = await createDynamicUser(dynamicCreateData)
          
          if (Array.isArray(result)) {
            console.log(`✅ ${result.length} usuarios dinámicos creados`)
          } else {
            console.log(`✅ Usuario dinámico creado con ID: ${result.data.id_asistente}`)
          }
        } else if (item.type === "create_dynamic_user_bulk") {
          // Handle bulk dynamic user creation
          const dynamicCreateDataArray = item.data as DynamicCreateRequest[]
          identifier = `Carga masiva de ${dynamicCreateDataArray.length} usuarios`
          const result = await createDynamicUser(dynamicCreateDataArray)
          
          if (Array.isArray(result)) {
            console.log(`✅ ${result.length} usuarios dinámicos creados en lote`)
          } else {
            console.log(`✅ Usuarios dinámicos creados en lote`)
          }
        } else if (item.type === "update_dynamic_user") {
          // Handle dynamic user update
          const dynamicUpdateData = item.data as DynamicUpdatePayload
          identifier = `Usuario único: ${dynamicUpdateData.uniqueFieldValue}`
          await updateDynamicUserByUniqueField(dynamicUpdateData)
          console.log(`✅ Usuario dinámico actualizado: ${dynamicUpdateData.uniqueFieldValue}`)
        } else if (item.type === "update_prefix") {
          // Handle prefix update
          const prefixData = item.data as PrefixUpdateData
          await updatePrefix(prefixData)
        } else if (item.type === "approve_category") {
          // Handle category approval
          const approveCategoryData = item.data as ApproveCategoryData
          await approveCategory(approveCategoryData)
        } else if (item.type === "change_category") {
          // Handle category change
          const changeCategoryData = item.data as ChangeCategoryData
          await changeCategory(changeCategoryData)
        } else {
          // Handle asistente updates
          const asistenteData = item.data as Asistente
          await updateAsistente(asistenteData)
        }

        await removeFromQueueDB(item.id)
        processed++

        console.log(`✅ Procesado: ${item.type} para ${identifier}`)
      } catch (error) {
        // Get identifier based on data type
        let identifier = ""
        if ("asistente_folio" in item.data) {
          identifier = item.data.asistente_folio
        } else if ("folio" in item.data) {
          identifier = item.data.folio
        } else if ("asistente_id" in item.data) {
          identifier = item.data.asistente_id
        } else if (Array.isArray(item.data)) {
          identifier = `Lote de ${item.data.length} usuarios`
        } else if ("nombre" in item.data) {
          identifier = `${item.data.nombre} ${item.data.apellido || ''}`
        }

        const errorMessage = error instanceof Error ? error.message : "Error desconocido"
        console.error(`❌ Error procesando ${item.type} para ${identifier}:`, error)

        // Verificar si es un error 207 (algunos usuarios no se pudieron crear)
        if (errorMessage.includes("207") || errorMessage.includes("Algunos usuarios no se pudieron crear")) {
          console.log(`🗑️ Error 207 detectado. Eliminando elemento de la cola para evitar reintentos: ${item.type} para ${identifier}`)
          await removeFromQueueDB(item.id)
          processed++
          // Continuar con el siguiente elemento en lugar de romper el procesamiento
          continue
        }

        lastError = errorMessage

        // Stop processing on first error (que no sea 207)
        break
      }
    }

    if (lastError) {
      return {
        success: false,
        processed,
        error: `Error después de procesar ${processed} elementos: ${lastError}`,
      }
    }

    console.log(`✅ Cola procesada exitosamente. ${processed} elementos procesados`)
    return { success: true, processed }
  } catch (error) {
    console.error("❌ Error general procesando cola:", error)
    return {
      success: false,
      processed: 0,
      error: error instanceof Error ? error.message : "Error desconocido",
    }
  }
}
